import React, { useState } from "react";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { BUSINESS_INFO } from "../config";
import { GallerySection } from "../components/GallerySection";
import { getOptimizedImageUrl } from "../lib/utils";

export function AboutPage() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleValue = (idx) => {
    setOpenIndex((prev) => (prev === idx ? null : idx));
  };

  const values = [
    {
      title: "Respekt for hundens grænser",
      desc: "Hunde kan ikke tale med ord, men de taler med hele kroppen. Vi lytter og observerer nøje. Hvis hunden har brug for en pause, vand eller en krammer, holder vi en pause.",
    },
    {
      title: "Trivsel før kosmetik",
      desc: "Et smukt kosmetisk resultat er skønt, men hundens velvære og tryghed vægtes altid højst. Vi arbejder med hunden og aldrig imod den.",
    },
    {
      title: "Faglig omhu & økologiske produkter",
      desc: "Jeg brænder for mit fag og holder mig løbende opdateret på de mest skånsomme klippeteknikker og ergonomisk håndtering. I salonen anvender jeg nøje udvalgte økologiske plejeprodukter, der er tilpasset den enkelte hund – til gavn for både hundens hud, pels og naturen.",
    },
    {
      title: "Personlig kontakt",
      desc: "Hos mig er det altid den samme faste person, der tager imod dig og din hund. Vi tager en god snak om hundens vaner, race og særlige behov.",
    },
  ];

  return (
    <>
      <SEO
        title="Om Hundens Pels og Pleje - Hundens Pels og Pleje"
        description="Læs om filosofien bag Hundens Pels og Pleje i Fredericia. Hundens grænser og trivsel vægtes altid højst med personlig omsorg og rolig atmosfære."
      />

      {/* Hero Header - No box behind heading and description */}
      <section className="relative bg-white pt-14 pb-24 sm:pt-20 sm:pb-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
            {/* Text */}
            <div className="lg:col-span-7 space-y-6">
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-black leading-tight">
                Om Hundens Pels og Pleje
              </h1>

              <p className="text-lg text-neutral-800 leading-relaxed font-normal">
                Hej og velkommen. Mit navn er Trine. Jeg er uddannet dyrepasser og uddannet hundefrisør. Jeg har arbejdet 8 år med dyr som fag og har altid været omgivet af dyr hele mit liv.
              </p>

              <p className="text-base text-neutral-700 leading-relaxed">
                Efter 12 år i ambulancetjenesten, hvor jeg arbejdede med mennesker, blev det helt tydeligt for mig, hvor mit hjerte for alvor hører til: hos dyrene.
              </p>

              <p className="text-base text-neutral-700 leading-relaxed">
                Arbejdet med hundene gør mig oprigtigt glad i hverdagen. For mig handler arbejdet som hundefrisør om meget mere end blot klip, vask og føn. Det handler om nærvær, tålmodighed og gensidig tillid, så hver enkelt hund får en tryg og rolig oplevelse i salonen.
              </p>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <a
                  href={BUSINESS_INFO.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 rounded-full bg-[#dabeb9] px-8 py-3.5 text-sm sm:text-base font-medium tracking-wide text-black shadow-xs transition-colors hover:bg-[#c9a7a0]"
                >
                  <i className="fa-solid fa-calendar-check text-black" aria-hidden="true" />
                  <span>Book tid til din hund</span>
                </a>

                {/* Secondary CTA: NO background color and NO hover background color */}
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-full border border-black bg-transparent px-6 py-3.5 text-sm sm:text-base font-medium tracking-wide text-black transition-colors hover:bg-transparent hover:text-black hover:border-black"
                >
                  <i className="fa-solid fa-location-dot text-black" aria-hidden="true" />
                  <span>Find salonen</span>
                </Link>
              </div>
            </div>

            {/* Image - STRICTLY NO TEXT ON IMAGE */}
            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-3xl shadow-sm ring-1 ring-[#E2D2C3]">
                <img
                  src={getOptimizedImageUrl(
                    "https://assets.auxodev.dk/assets/9e509bf9-9216-49e7-9224-6ccd4f39c4eb/1000003489.jpg",
                    { width: 800, quality: 85 }
                  )}
                  alt="Uddannet hundefrisør Trine ved trimmebord med lille hund, eksamensdiplom og blomster"
                  className="h-[400px] w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#FEFAF3]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-black">
              Det kan du forvente hos mig
            </h2>

            <p className="mt-3 text-base text-neutral-700">
              En personlig, varm og omsorgsfuld tilgang, hvor din hund altid er i trygge hænder.
            </p>
          </div>

          {/* Cards: NO icons, expandable */}
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
            {values.map((val, idx) => {
              const isOpen = openIndex === idx;
              return (
                <div
                  key={idx}
                  onClick={() => toggleValue(idx)}
                  className="cursor-pointer rounded-3xl border border-[#E8DFD5]/80 bg-white p-6 sm:p-8 shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] transition-all duration-200"
                >
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="font-heading text-lg font-medium text-black">
                      {val.title}
                    </h3>
                    <i
                      className={`fa-solid fa-chevron-down text-black transition-transform duration-200 shrink-0 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                      aria-hidden="true"
                    />
                  </div>

                  {isOpen && (
                    <div className="mt-4 pt-4 border-t border-neutral-100">
                      <p className="text-sm text-neutral-700 leading-relaxed">
                        {val.desc}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Large Quote Block - clean styling, no box */}
          <div className="mt-16 text-center max-w-3xl mx-auto">
            <p className="font-heading text-xl sm:text-2xl text-black leading-relaxed font-medium">
              ”Kundens ønsker er vigtige, men hensynet til hunden vægtes altid højst.”
            </p>
            <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-black">
              – Filosofien hos Hundens Pels og Pleje
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-full bg-[#dabeb9] px-7 py-3.5 text-sm font-medium tracking-wide text-black shadow-xs transition-colors hover:bg-[#c9a7a0]"
              >
                <i className="fa-solid fa-comment-dots text-black" aria-hidden="true" />
                <span>Kontakt salonen</span>
              </Link>
              <a
                href={BUSINESS_INFO.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-black bg-transparent px-7 py-3.5 text-sm font-medium tracking-wide text-black transition-colors hover:bg-black hover:text-white"
              >
                <i className="fa-solid fa-calendar-check text-current" aria-hidden="true" />
                <span>Book tid online</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY SECTION */}
      <GallerySection
        categorySlug="foer-efter"
        title="Billeder af vores glade gæster"
        subtitle="Se de fine resultater og de mange dejlige racer, som har besøgt salonen."
        showTabs={false}
      />
    </>
  );
}
